import { Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '@/components/ui'
import { ModuleEmptyPage } from '@/components/layout/ModuleEmptyPage'
import { useSession } from '@/features/auth/session'
import { useCurrentMembership } from '@/features/tenants/queries'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'

import { AccountCard } from './components/AccountCard'
import { DangerZoneCard } from './components/DangerZoneCard'
import { HandoffPolicyCard } from './components/HandoffPolicyCard'
import { LanguageCard } from './components/LanguageCard'
import { OrganizationCard } from './components/OrganizationCard'
import { SecurityCard } from './components/SecurityCard'
import { TriageSettingsCard } from './components/TriageSettingsCard'

/**
 * Configurações da organização: dados da organização (nome/identificador/status read-only + site
 * editável), idioma da interface e a conta do usuário logado. Só usa backend existente — nenhuma
 * funcionalidade fingida. Vocabulário neutro (docs/design/00 §5).
 */
export function SettingsPage() {
  const { t } = useTranslation()
  const { tenant, isLoading } = useCurrentTenant()
  const { user } = useSession()
  // Danger zone é Owner-only: derivamos o role do usuário atual (só carrega para Owner/Administrator).
  const { membership } = useCurrentMembership(tenant?.id)
  const isOwner = membership?.role === 'Owner'

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t('loading')} />
      </div>
    )
  }

  if (tenant === null) {
    return (
      <ModuleEmptyPage
        icon={Settings}
        title={t('modules.settings.title')}
        description={t('modules.settings.description')}
      />
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground-primary">{t('settings.pageTitle')}</h1>
        <p className="text-sm text-foreground-muted">{t('settings.pageSubtitle')}</p>
      </header>

      <OrganizationCard tenant={tenant} />
      <HandoffPolicyCard tenantId={tenant.id} />
      <TriageSettingsCard tenantId={tenant.id} />
      <LanguageCard />
      {user !== null ? <AccountCard user={user} /> : null}
      <SecurityCard />
      {isOwner ? <DangerZoneCard tenant={tenant} /> : null}
    </div>
  )
}
